import { faCircleXmark } from '@fortawesome/pro-duotone-svg-icons/faCircleXmark';
import { faFilePdf } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { CircularProgress, Modal } from '@mui/material';
import noop from 'lodash/noop';
import React, { CSSProperties, useState } from 'react';
import styled from 'styled-components';
import useTracking from '../../hooks/useTracking';
import { isImageFileType } from '../../hooks/useUploadFile';
import { BLACK, BORDER_GRAY, COOL_GRAY100, WHITE } from '../../theme';
import {
  EventCategories,
  EventNames,
  EventSubCategories,
} from '../../utils/trackingEvents';
import { ButtonSize, ButtonVariant } from '../reusable/Button';
import { IconButton } from '../reusable/Button/IconButton';
import { ColorIcon } from '../reusable/ColorIcon';
import Text, { TextKind } from '../reusable/Text';
import { TextLineClamp } from '../reusable/TextLineClamp';
import { UnstyledButton } from '../reusable/UnstyledButton';

const StyledImagePreviewContainer = styled.div`
  display: flex;
  flex: 1;
  max-width: 100%;
  position: relative;
  border: 1px solid ${BORDER_GRAY};
  border-radius: 16px;
  padding: 16px 16px 4px;
`;

const StyledFilePreviewContainer = styled.div`
  display: flex;
  flex: 1;
  max-width: 100%;
  position: relative;
  border: 1px solid ${BORDER_GRAY};
  border-radius: 16px;
  padding: 8px 12px;
`;

const StyledLoadingContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.3);
  opacity: 0.5;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const AbsolutePositioned = styled.div<{ $top?: string; $right?: string }>`
  position: absolute;
  top: ${({ $top }) => $top || '0'};
  right: ${({ $right }) => $right || '0'};
`;

const ExpandedImageContainer = styled.div`
  position: relative;
  max-width: 100%;
`;

const StyledImageContainer = styled.div`
  position: relative;
  img {
    height: auto;
    width: auto;
    max-height: 100%;
    max-width: 100%;
    border-radius: 8px;
  }
`;

const StyledFileContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StyledPreviewImage = styled.img`
  width: auto;
  height: auto;
  max-width: 100%;
`;
const StyledPopupImg = styled.img`
  max-width: calc(100vw - 32px);
  max-height: calc(100vh - 32px);
  background: ${WHITE};
`;

const duotoneColors = {
  '--fa-primary-color': WHITE,
  '--fa-secondary-color': BLACK,
  '--fa-secondary-opacity': '1',
};

const CloseButton: React.FC<{ onClick: () => void; ariaLabel: string }> = ({
  onClick,
  ariaLabel,
}) => (
  <IconButton
    size={ButtonSize.ExtraSmall}
    variant={ButtonVariant.Dark}
    onClick={onClick}
    ariaLabel={ariaLabel}
    icon={
      <FontAwesomeIcon
        icon={faCircleXmark}
        fontSize={24}
        style={duotoneColors}
      />
    }
  />
);

export const Image = ({
  src,
  isClickable,
  chatId,
  messageId,
  fileName,
  previewStyle,
}: {
  src: string;
  fileName: string;
  isClickable?: boolean;
  chatId?: string | null;
  messageId?: string;
  previewStyle?: CSSProperties;
}) => {
  const { track } = useTracking();
  const [showModal, setShowModal] = useState(false);
  const openModal = () => {
    setShowModal(true);
    track(EventNames.ImageClick, {
      event_category: EventCategories.Chat,
      event_subcategory: EventSubCategories.Image,
      chat_id: chatId,
      message_id: messageId,
    });
  };
  const closeModal = () => {
    setShowModal(false);
    track(EventNames.CloseClick, {
      event_category: EventCategories.Chat,
      event_subcategory: EventSubCategories.Image,
      chat_id: chatId,
      message_id: messageId,
    });
  };
  return (
    <>
      <UnstyledButton
        onClick={isClickable ? openModal : noop}
        disabled={!isClickable}
        aria-label="expand image"
      >
        <StyledPreviewImage
          src={src}
          alt={`image preview for ${fileName}`}
          style={previewStyle}
        />
      </UnstyledButton>
      <Modal
        open={showModal}
        onClose={closeModal}
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <ExpandedImageContainer>
          <AbsolutePositioned $top="-12px" $right="-12px">
            <CloseButton onClick={closeModal} ariaLabel="close image modal" />
          </AbsolutePositioned>
          <StyledPopupImg src={src} alt={`expanded image for ${fileName}`} />
        </ExpandedImageContainer>
      </Modal>
    </>
  );
};

export const FilePreview = ({
  file,
  loading,
  onRemoveFile,
  previewStyle,
}: {
  file: File;
  loading: boolean;
  onRemoveFile: () => void;
  previewStyle?: CSSProperties;
}) => {
  const url = URL.createObjectURL(file);

  if (isImageFileType(file.type)) {
    return (
      <StyledImagePreviewContainer>
        {loading && (
          <StyledLoadingContainer data-testid="file-preview-loading">
            <CircularProgress size={48} />
          </StyledLoadingContainer>
        )}
        <StyledImageContainer>
          <Image
            fileName={file.name}
            src={url}
            previewStyle={previewStyle ?? {}}
          />
        </StyledImageContainer>
        {!loading && (
          <AbsolutePositioned $top="-6px" $right="-6px">
            <CloseButton ariaLabel="Remove file" onClick={onRemoveFile} />
          </AbsolutePositioned>
        )}
      </StyledImagePreviewContainer>
    );
  }

  return (
    <StyledFilePreviewContainer>
      {loading && (
        <StyledLoadingContainer data-testid="file-preview-loading">
          <CircularProgress size={48} />
        </StyledLoadingContainer>
      )}
      <StyledFileContainer data-testid="file-preview">
        <ColorIcon
          icon={<FontAwesomeIcon icon={faFilePdf} />}
          backgroundColor={COOL_GRAY100}
        />
        <TextLineClamp $lines={2}>
          <Text kind={TextKind.LabelL}>{file.name}</Text>
        </TextLineClamp>
      </StyledFileContainer>
      {!loading && (
        <AbsolutePositioned $top="-6px" $right="-6px">
          <CloseButton ariaLabel="Remove file" onClick={onRemoveFile} />{' '}
        </AbsolutePositioned>
      )}
    </StyledFilePreviewContainer>
  );
};
